import { notifyBugsnagFromErrorBoundary } from '@salescore/client-base'
import { Button, message, Select, Table } from 'antd'
import { type ReactElement, Suspense } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'

import { Loading } from './Loading'

type FallbackType = 'default' | 'table' | 'select' | 'button' | 'none' | 'custom'

const ErrorFallback = ({ error }: FallbackProps) => (
  <div className="text-red-500">
    <p>
      エラーが発生しました。
      {error instanceof Error ? error.message : '不明なエラー'}
    </p>
  </div>
)

const ErrorBoundaryForSuspense = ({ children }: { children: ReactElement }): ReactElement => (
  <ErrorBoundary
    fallbackRender={ErrorFallback}
    onError={(error) => {
      // 認証前に呼ばれることがあるためi18n対応しない
      if (error instanceof Error) {
        void message.error(`エラーが発生しました。${error.message}`)
        notifyBugsnagFromErrorBoundary({ error })
      } else {
        void message.error(`エラーが発生しました。`)
        notifyBugsnagFromErrorBoundary({ error: new Error('unknown error') })
      }
    }}
  >
    {children}
  </ErrorBoundary>
)

//
// ApolloProviderやRecoilRootよりも上の要素で使っているので、これらの要素に依存させないこと
// （依存させたい場合、DefaultProviderのロジックを変更すること）
//
export const SuspenseWithLoading = ({
  children,
  type,
  loadingText,
  loadingElement,
}: {
  children: ReactElement
  type?: FallbackType
  loadingText?: string
  loadingElement?: ReactElement
}): ReactElement => {
  switch (type) {
    case 'table': {
      return (
        <ErrorBoundaryForSuspense>
          <Suspense fallback={<Table loading />}>{children}</Suspense>
        </ErrorBoundaryForSuspense>
      )
    }
    case 'select': {
      return (
        <ErrorBoundaryForSuspense>
          <Suspense fallback={<Select loading />}>{children}</Suspense>
        </ErrorBoundaryForSuspense>
      )
    }
    case 'button': {
      return (
        <ErrorBoundaryForSuspense>
          <Suspense fallback={<Button loading>{loadingText ?? ''}</Button>}>{children}</Suspense>
        </ErrorBoundaryForSuspense>
      )
    }
    case 'none': {
      // ErrorBoundaryForSuspenseを使うだけ
      // このtypeを選択するなら、わざわざこのコンポーネントを使う意味があまりないが、共通化のため一応定義しておく
      return (
        <ErrorBoundaryForSuspense>
          <Suspense>{children}</Suspense>
        </ErrorBoundaryForSuspense>
      )
    }
    case 'custom': {
      return (
        <ErrorBoundaryForSuspense>
          <Suspense fallback={loadingElement}>{children}</Suspense>
        </ErrorBoundaryForSuspense>
      )
    }
    default: {
      break
    }
  }

  return (
    <ErrorBoundaryForSuspense>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </ErrorBoundaryForSuspense>
  )
}
